import { useState, useEffect } from 'react';
import { AlertTriangle, Pill, Calendar, ArrowRight } from 'lucide-react';
import { clsx } from 'clsx';
import api from '../config/api';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

const PAGE_SIZE = 50;

export default function ExpiryAlerts() {
  const [alerts, setAlerts] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadAlerts();
  }, [page]);

  const loadAlerts = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/alerts/expiry?page=${page}&limit=${PAGE_SIZE}`);
      const list = res?.alerts || [];
      setAlerts(list.map((alert) => ({
        ...alert,
        daysLeft: Number(alert.days_left),
        severity: Number(alert.days_left) <= 0 ? 'expired' : Number(alert.days_left) <= 30 ? 'critical' : Number(alert.days_left) <= 60 ? 'warning' : 'caution',
      })));
      setTotal(res?.pagination?.total ?? list.length);
      setPages(res?.pagination?.pages ?? 1);
    } catch (e) {
      toast.error(e.message || 'Failed to load expiry alerts');
    } finally {
      setLoading(false);
    }
  };

  const severityConfig = {
    expired: { bg: 'bg-red-50 border-red-500', badge: 'bg-red-600 text-white', dot: 'bg-red-600', label: 'Expired' },
    critical: { bg: 'bg-red-50 border-red-200', badge: 'bg-red-100 text-red-700', dot: 'bg-red-500', label: 'Critical' },
    warning: { bg: 'bg-amber-50 border-amber-200', badge: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500', label: 'Warning' },
    caution: { bg: 'bg-orange-50 border-orange-200', badge: 'bg-orange-100 text-orange-700', dot: 'bg-orange-500', label: 'Caution' },
  };

  const expiredCount = alerts.filter((a) => a.severity === 'expired').length;
  const criticalCount = alerts.filter((a) => a.severity === 'critical').length;
  const warningCount = alerts.filter((a) => a.severity === 'warning' || a.severity === 'caution').length;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-500" />
            <h2 className="text-base font-semibold text-gray-900">Expiry Alerts</h2>
          </div>
          <div className="flex items-center gap-2">
            {expiredCount > 0 && (
              <span className="rounded-full bg-red-600 px-2.5 py-1 text-[11px] font-bold text-white">
              {expiredCount} Expired on page
              </span>
            )}
            <span className="rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-bold text-red-700">
            {criticalCount} Critical on page
            </span>
            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-700">
            {warningCount} Warning on page
            </span>
          </div>
        </div>

        {loading ? (
          <div className="py-16 text-center text-sm text-gray-400">Loading expiry alerts...</div>
        ) : alerts.length > 0 ? (
          <div className="divide-y divide-gray-100">
            {alerts.map((alert) => {
              const config = severityConfig[alert.severity] || severityConfig.caution;
              return (
                <div key={alert.id} className={clsx('border-l-4 p-5 transition-colors hover:bg-gray-50/50', alert.severity === 'expired' ? 'border-l-red-600 bg-red-50/20' : alert.severity === 'critical' ? 'border-l-red-500' : alert.severity === 'warning' ? 'border-l-amber-500' : 'border-l-orange-500')}>
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={clsx('mt-0.5 flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full', alert.severity === 'expired' ? 'bg-red-200' : alert.severity === 'critical' ? 'bg-red-100' : alert.severity === 'warning' ? 'bg-amber-100' : 'bg-orange-100')}>
                        <Pill className={clsx('h-5 w-5', alert.severity === 'expired' ? 'text-red-700' : alert.severity === 'critical' ? 'text-red-600' : alert.severity === 'warning' ? 'text-amber-600' : 'text-orange-600')} />
                      </div>
                      <div>
                        <h3 className="text-sm font-semibold text-gray-900">{alert.medicine_name}</h3>
                        <p className="text-xs text-gray-500">{alert.generic_name}</p>
                        <div className="mt-2 flex items-center gap-3 text-xs text-gray-600">
                          <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />Expires: {alert.expiry_date}</span>
                          <span className="flex items-center gap-1">Qty: {alert.quantity} {alert.unit}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className="text-right">
                        <span className={clsx('inline-flex rounded-full px-3 py-1 text-[11px] font-bold', config.badge)}>
                          {alert.daysLeft < 0 ? `Expired ${Math.abs(alert.daysLeft)} days ago` : alert.daysLeft === 0 ? 'Expires today (0 days left)' : `${alert.daysLeft} days left`}
                        </span>
                        <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">{config.label}</p>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-16 text-gray-400">
            <AlertTriangle className="mb-3 h-12 w-12 text-gray-300" />
            <p className="text-sm">No expiry alerts</p>
            <p className="text-xs text-gray-400 mt-1">All medicines are within their shelf life</p>
          </div>
        )}
        {total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
            <p className="text-sm text-gray-500">Showing {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total.toLocaleString()}</p>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setPage((current) => Math.max(1, current - 1))} disabled={page === 1 || loading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">Previous</button>
              <span className="text-sm font-medium text-gray-600">Page {page} of {pages}</span>
              <button type="button" onClick={() => setPage((current) => Math.min(pages, current + 1))} disabled={page >= pages || loading} className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-700 disabled:cursor-not-allowed disabled:opacity-50">Next</button>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => navigate('/medicines')}
        className="flex items-center gap-2 text-sm font-semibold text-emerald-600 hover:text-emerald-700"
      >
        View All Medicines <ArrowRight className="h-4 w-4" />
      </button>
    </div>
  );
}
